import React, { useCallback, useEffect, useState } from "react";

import AlertCircle from "@uui/AlertCircle";
import { debounce } from "lodash/fp";

import { Divider, Typography } from "@mui/material";

import {
  LicenseCountButton,
  LicenseCountInput,
} from "@app/analytics/account/Billing/components/styles";
import usePaymentMethod from "@app/analytics/account/Billing/hooks/usePaymentMethod";
import usePreviewInvoice from "@app/analytics/account/Billing/hooks/usePreviewInvoice";
import {
  getDueDateFromInvoice,
  getProratedPriceBreakdown,
  getTaxRateFromInvoice,
  isFreePlan,
} from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useBillingSubscription from "@app/analytics/hooks/billing/useBillingSubscription";
import * as Events from "@app/analytics/tracking/events";
import { toast } from "@app/common/hooks/useToasts";
import {
  isResponseErrorWithStatus,
  pluralize,
  pluralizePhrase,
} from "@app/common/utils";
import type { EnterpriseWorkspace } from "@app/common/workspaces/types";
import Config from "@app/config";
import {
  formatCurrency,
  formatCurrencyFromCents,
  formatNumber,
} from "@app/utils/utils";

import ConfirmationModal, {
  ConfirmationModalProps,
} from "@rui/ConfirmationModal";
import InfoPanel from "@rui/InfoPanel";
import LoadSpinner from "@rui/LoadSpinner";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

interface AddLicensesModalProps
  extends Pick<ConfirmationModalProps, "open" | "onClose"> {
  workspaceId?: EnterpriseWorkspace["id"];
}

const DEFAULT_MIN_LICENSES_TO_ADD = 0;

const AddLicensesModal = ({
  onClose,
  open,
  workspaceId,
}: AddLicensesModalProps) => {
  const { isTrial, licenses, subscription } = useBillingContext();
  const { updateSubscriptionQuantity } = useBillingSubscription();
  const { hasPaymentMethod } = usePaymentMethod();

  const minimumLicensesForPlan =
    Config.billing.licenseMinimum[
      subscription.scheduledUpdate?.plan ?? subscription.plan
    ] ?? 1;

  const minimumAllowableLicenses = Math.max(
    minimumLicensesForPlan,
    licenses.numClaimed
  );

  // shouldn't ever happen, but account for any scenario where the user somehow has more people
  // in their workspace than licenses they have purchased, or has fewer licenses than is allowed
  // by their plan. in either case, never allow them to purchase fewer licenses than what it would
  // take to get them back to valid state.
  const minLicensesToAdd = Math.max(
    DEFAULT_MIN_LICENSES_TO_ADD,
    minimumAllowableLicenses - licenses.numActive
  );

  const [numToAdd, setNumToAdd] = useState(minLicensesToAdd);
  const [previewInvoiceDelta, setPreviewInvoiceDelta] = useState(numToAdd);

  // only allow the user to purchase as many licenses as are available for purchase, per
  // their subscription settings. default to reasonable limit
  const maximumNumToAdd =
    licenses.numAvailableForPurchase ?? Config.billing.maxSeatAdditions;

  // we will not show a license cost preview if
  // 1. they are currently on a free plan
  // 2. they are on a free trial and have no payment information saved (subscription will cancel at end of trial)
  // 3. they are on a free trial and will downgrade to a free plan at the end of the trial
  const willPayForLicenses =
    !isTrial &&
    !isFreePlan(subscription.plan) &&
    (!subscription.scheduledUpdate ||
      !isFreePlan(subscription.scheduledUpdate.plan));

  // if the user is on a paid plan (and not in a trial), they can only purchase additional licenses if they have
  // a payment method on file, unless they are "purchasing" ones that they have paid for but since removed
  const cannotPurchaseAdditionalLicenses =
    !subscription.inTrialPeriod &&
    !hasPaymentMethod &&
    !isFreePlan(subscription.plan);

  const maxPurchasableLicenses = cannotPurchaseAdditionalLicenses
    ? licenses.numPaid - licenses.numActive
    : undefined;

  // the user can never purchase licenses if they're on the Free plan, if their subscription does
  // not allow purchasing any more licenses than they already have, or if the user does not have any
  // paid (but inactive) licenses that they can simply reactivate
  const cannotAddLicenses =
    isFreePlan(subscription.plan, true) ||
    maximumNumToAdd === 0 ||
    (cannotPurchaseAdditionalLicenses && maxPurchasableLicenses === 0);

  const { isError, isLoading, previewInvoice, previewInvoiceError } =
    usePreviewInvoice({
      delta: previewInvoiceDelta,
      enabled: open && willPayForLicenses && !cannotAddLicenses,
    });

  const debouncedUpdatePreviewInvoiceQuery = useCallback(
    debounce(500, setPreviewInvoiceDelta),
    []
  );

  useEffect(() => {
    // limit the number of times we're fetching the invoice if the user
    // is changing the value a bunch; 500ms is short enough that
    // it doesn't feel like an odd delay
    debouncedUpdatePreviewInvoiceQuery(numToAdd);
  }, [numToAdd]);

  const handleUpdateLicenseCount = (
    update: { delta: -1 | 1 } | { count: number }
  ) => {
    setNumToAdd((prevNum) =>
      "count" in update
        ? Math.max(update.count, minLicensesToAdd)
        : prevNum + update.delta
    );
  };

  const handleIncrementDecrementLicenseCount = (delta: -1 | 1) => () => {
    handleUpdateLicenseCount({ delta });
  };

  const handleChangeLicenseCount: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const count = parseInt(event.target.value);
    handleUpdateLicenseCount({
      count: Math.min(
        isNaN(count) ? minLicensesToAdd : count,
        Config.billing.maxSeatAdditions
      ),
    });
  };

  const handleClose = () => {
    onClose();
    setNumToAdd(minLicensesToAdd);
  };

  const handleConfirm = () => {
    updateSubscriptionQuantity.mutate(numToAdd, {
      onSuccess: () => {
        Events.addPlanLicenses({ seats: numToAdd });
        toast.success("Licenses updated!");
      },
      onError: (error) => {
        toast.error(
          isResponseErrorWithStatus(error, 402)
            ? "Payment failed. Please review your billing details or try again with a different payment method."
            : "Oops! Something went wrong and your update could not be processed. Please refresh the page or try again later."
        );
      },
      onSettled: handleClose,
    });
  };

  const priceBreakdown = willPayForLicenses
    ? getProratedPriceBreakdown(subscription)
    : undefined;

  const hasError = previewInvoiceError != null || isError;
  const overTransactionLimit = previewInvoiceError === "over_transaction_limit";

  const confirmDisabled =
    cannotAddLicenses ||
    overTransactionLimit ||
    (willPayForLicenses
      ? hasError || isLoading || !previewInvoice
      : numToAdd > maximumNumToAdd) ||
    numToAdd < 1;
  const taxRate = getTaxRateFromInvoice(previewInvoice);
  const dueDate = getDueDateFromInvoice(previewInvoice);

  const hasInactiveLicenses = licenses.numInactive > 0;

  return (
    <ConfirmationModal
      loading={updateSubscriptionQuantity.isPending}
      open={open}
      onClose={handleClose}
      size="xl"
      title="Add Licenses"
      onConfirm={handleConfirm}
      confirmProps={{
        label: willPayForLicenses ? "Purchase licenses" : "Add licenses",
        disabled: confirmDisabled,
        tooltip: overTransactionLimit
          ? "Reduce the number of licenses to complete this purchase. You may purchase additional licenses as a separate transaction."
          : hasError
            ? `Your subscription must have at least ${pluralizePhrase(
                minimumAllowableLicenses,
                "license"
              )}`
            : undefined,
      }}
    >
      {cannotAddLicenses && (
        <InfoPanel severity="error">
          {isFreePlan(subscription.plan)
            ? "Upgrade to Read Pro or Enterprise to purchase additional licenses."
            : "Connect a payment method to purchase additional licenses."}
        </InfoPanel>
      )}
      <Stack spacing={2} sx={{ opacity: cannotAddLicenses ? 0.6 : 1 }}>
        <Typography variant="bodyPrimaryReg">
          You currently have{" "}
          <Bold>
            {hasInactiveLicenses ? formatNumber(licenses.numInactive) : "no"}{" "}
            {pluralize("inactive license", licenses.numInactive)}
          </Bold>
          {workspaceId ? " in your Workspace" : ""}
          {hasInactiveLicenses ? (
            <>
              {" "}
              through the end of this billing period. Adding up to{" "}
              {formatNumber(licenses.numInactive)}{" "}
              {pluralize("license", licenses.numInactive)} will not incur any
              additional cost through the remainder of the current billing
              period.
            </>
          ) : (
            <>
              .
              {willPayForLicenses &&
                " You will be charged a prorated amount for the total number of licenses added below."}
            </>
          )}
        </Typography>
        {subscription.inTrialPeriod && willPayForLicenses && (
          <InfoPanel severity="warning">
            Since you are on a free trial, the prorated cost shown below will
            include your {pluralizePhrase(licenses.numPaid, "license")}{" "}
            previously purchased. You will not be double charged for{" "}
            {pluralize("this license", "these licenses", licenses.numPaid)}.
          </InfoPanel>
        )}
        <Divider />
        <Stack sx={{ color: "shades.neutral.500" }} spacing={2}>
          <Stack
            direction={{ xxs: "column", sm: "row" }}
            alignItems={{ xxs: "flex-start", sm: "center" }}
            justifyContent={{ xxs: "center", sm: "space-between" }}
            px={1}
            spacing={1}
          >
            <Stack>
              <Typography variant="bodyPrimaryReg" color="shades.neutral.800">
                Number of licenses to add
              </Typography>
              {priceBreakdown?.pricePerLicense != null && (
                <Typography variant="bodySecondaryReg">
                  {formatCurrency(priceBreakdown.pricePerLicense, {
                    digits: 0,
                  })}{" "}
                  x {formatNumber(numToAdd)} {pluralize("license", numToAdd)} x{" "}
                  {priceBreakdown.remainingTime}
                </Typography>
              )}
            </Stack>
            <Row>
              <LicenseCountButton
                kind="decrement"
                onClick={handleIncrementDecrementLicenseCount(-1)}
                disabled={numToAdd === minLicensesToAdd}
              />
              <LicenseCountInput
                onChange={handleChangeLicenseCount}
                value={numToAdd}
                min={minLicensesToAdd}
                max={maximumNumToAdd}
              />
              <LicenseCountButton
                kind="increment"
                onClick={handleIncrementDecrementLicenseCount(1)}
                disabled={numToAdd >= maximumNumToAdd}
              />
            </Row>
          </Stack>
          {willPayForLicenses && (
            <>
              <Divider />
              <Stack spacing={1.5} sx={{ px: 1.5 }}>
                <Row variant="apart" spacing={1}>
                  <Typography variant="bodyPrimaryMed">
                    Prorated cost for current billing period
                  </Typography>
                  {previewInvoice ? (
                    <Typography variant="bodyPrimaryMed" noWrap flexShrink={0}>
                      {formatCurrencyFromCents(previewInvoice.subtotal)}
                    </Typography>
                  ) : hasError ? (
                    <AlertCircle
                      sx={{ color: "error.main", fontSize: "16px" }}
                    />
                  ) : isLoading ? (
                    <LoadSpinner size={12} />
                  ) : (
                    <Typography variant="bodyPrimaryMed">-</Typography>
                  )}
                </Row>
                <Divider />
                <Row variant="apart" spacing={1}>
                  <Typography variant="bodyPrimaryMed">
                    Sales Tax{taxRate && ` (${taxRate}%)`}
                  </Typography>
                  {previewInvoice ? (
                    <Typography variant="bodyPrimaryMed" noWrap flexShrink={0}>
                      {formatCurrencyFromCents(previewInvoice.tax ?? 0)}
                    </Typography>
                  ) : hasError ? (
                    <AlertCircle
                      sx={{ color: "error.main", fontSize: "16px" }}
                    />
                  ) : isLoading ? (
                    <LoadSpinner size={12} />
                  ) : (
                    <Typography variant="bodyPrimaryMed">-</Typography>
                  )}
                </Row>
                <Divider />
                <Row variant="apart" spacing={1}>
                  <Typography variant="bodyPrimaryMed">
                    Total{dueDate ? ` due ${dueDate}` : ""}
                  </Typography>
                  {previewInvoice ? (
                    <Typography variant="bodyPrimaryMed" noWrap flexShrink={0}>
                      {formatCurrencyFromCents(previewInvoice.total)}
                    </Typography>
                  ) : hasError ? (
                    <AlertCircle
                      sx={{ color: "error.main", fontSize: "16px" }}
                    />
                  ) : isLoading ? (
                    <LoadSpinner size={12} />
                  ) : (
                    <Typography variant="bodyPrimaryMed">-</Typography>
                  )}
                </Row>
              </Stack>
            </>
          )}
        </Stack>
        <Divider />
      </Stack>
    </ConfirmationModal>
  );
};

export default AddLicensesModal;
